import { createRootRoute, HeadContent, Link, Outlet, Scripts } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { BUG_REPORT_ENABLED } from "@/features/bug-report/enabled";
import { i18n } from "@/lib/i18n";
import { NAV_DIRECTION_DEFAULT, VEIL_ID } from "@/lib/screenTransitions";
import { ORG_NAME } from "@/lib/seo/head";
import { THEME_COLOR } from "@/lib/useThemeColor";
import "@/index.css";
// Content-hashed URLs of the two critical self-hosted faces — the body (Inter)
// and display (Space Grotesk) `latin` variable woff2s — preloaded in the head
// below so they start fetching before the CSS that references them is parsed
// (ADR-0053 §6). Vite dedupes these against the same files referenced from
// fonts.css, so preload + @font-face point at one asset.
import interLatinUrl from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import spaceGroteskLatinUrl from "@fontsource-variable/space-grotesk/files/space-grotesk-latin-wght-normal.woff2?url";

// The staging bug reporter, behind a build-time constant AND a dynamic import. The
// constant alone would leave a dead branch; `lazy` also keeps the widget and its
// screenshot library out of the entry bundle — and, because the module is then
// touched only when it actually renders, out of every test that merely imports this
// root route (the shared vitest setup does exactly that).
const BugReportWidget = BUG_REPORT_ENABLED
  ? lazy(async () => ({
      default: (await import("@/features/bug-report/BugReportWidget")).BugReportWidget,
    }))
  : null;

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <h1 className="text-3xl font-semibold tracking-tight">{t("notFound.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("notFound.description")}</p>
      <Link to="/" className="mt-6 inline-block text-sm underline">
        {t("nav.backHome")}
      </Link>
    </div>
  );
}

// The document shell rendered around every route (ADR-0053 §1). Under the
// build-time prerender it produces the real `<html>`/`<head>`/`<body>` that ships
// as static HTML; on the client the same shell is what `hydrateRoot(document)`
// hydrates. `HeadContent` bakes the merged per-route head (the base tags below +
// the route's own `<title>`), and `Scripts` injects the client entry — replacing
// the hand-written `index.html` + `<script src=main.tsx>` this app used to ship.
function RootDocument({ children }: { children: ReactNode }) {
  return (
    // `data-nav-direction` is rendered here (not only written imperatively) because
    // `installScreenTransitions` sets it inside the router factory, BEFORE hydration —
    // without it in the shell React sees an attribute the client tree lacks and reports a
    // hydration mismatch on `<html>`. Constant on purpose: navigations move it imperatively
    // (lib/screenTransitions) and React never re-renders this prop, so it is not clobbered.
    <html lang="pl" data-nav-direction={NAV_DIRECTION_DEFAULT}>
      <head>
        <HeadContent />
      </head>
      <body className="bg-paper">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // Browser-language detection is a client-boot concern, driven from the client
  // entry (`client.tsx`) after hydration — NOT here — so it runs once per app
  // load and never fires under the unit tests, which render this tree directly
  // and drive the language themselves.
  return (
    <>
      <Layout>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
        <Toaster />
      </Layout>
      <ScreenTransitionVeil />
      {/* Staging-only bug reporter. Mounted HERE, outside <Layout>, because Layout
          returns three different shells (fullscreen Stage / chrome / bare) and the
          edge tab must be reachable from every route — one mount point instead of
          three. */}
      {BugReportWidget ? (
        <Suspense fallback={null}>
          <BugReportWidget />
        </Suspense>
      ) : null}
    </>
  );
}

// The overlay veil for the fade-through crossings (ADR-0052, full-bleed ↔ chrome). A single
// fixed, edge-to-edge, pointer-transparent layer above EVERYTHING (nav, toasts, the Stage
// canvas) whose opacity `installScreenTransitions` animates: fade IN to cover the old page,
// swap the route while covered, fade OUT to reveal the new one. It lives OUTSIDE <Layout> so
// no route-chrome ancestor's transform/filter can turn `position: fixed` into a clipped
// containing block, and it persists across route swaps (only <Outlet> changes), so its
// running animation is never torn down mid-transition. Starts fully transparent + inert; it
// only ever has an effect while a fade-through crossing drives it. Driven imperatively by id
// (VEIL_ID) rather than React state so the animation is decoupled from the route commit it
// is covering. Why an overlay and not the native root cross-fade: WebKit mis-scales the root
// snapshot on these mismatched layouts (see lib/screenTransitions → isFullBleedChromeCrossing).
function ScreenTransitionVeil() {
  return (
    <div
      id={VEIL_ID}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        opacity: 0,
        pointerEvents: "none",
        zIndex: 2147483647,
        backgroundColor: THEME_COLOR.paper,
      }}
    />
  );
}

export const Route = createRootRoute({
  // Base head shared by every route (ADR-0053 §1). Per-route heads (e.g. the
  // landing's real `<title>`) merge on top via `HeadContent`. Fonts are now
  // self-hosted (ADR-0053 §6): the cross-origin Google Fonts stylesheet +
  // preconnects are gone (no render-blocking waterfall, no IP leak to Google —
  // RODO / ADR-0050), replaced by @font-face in fonts.css + the preloads below.
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
      },
      // Sitewide DEFAULT title — a last-resort brand fallback so no route ever
      // ships titleless. Every real route overrides it: the public pages via
      // `buildHead`, the app shells via `appShellTitle` (both merge on top through
      // `HeadContent`, last title wins). In practice only the dev-only harnesses
      // (/shot, /geomshot, /stage-lab) fall through to this.
      { title: `${ORG_NAME} · ${i18n.t("app.tagline")}` },
      // Tints the mobile browser chrome; kept in sync with the active route by
      // useThemeColor (paper on light pages, dark-warm Stage on the viewer).
      { name: "theme-color", content: THEME_COLOR.paper },
    ],
    links: [
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      // Preload only the two critical faces (body + display, `latin`). `latin-ext`
      // (Polish diacritics) and the pet-name faces load on CSS discovery. `crossOrigin`
      // is required even for same-origin font preloads or the browser double-fetches.
      // NOT preloading `latin-ext` is deliberate (lowest critical-path byte cost): a
      // brief two-stage paint on Polish diacritics is acceptable because the
      // metric-matched fallback (fonts.css) holds the layout — confirm in the visual sign-off.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interLatinUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: spaceGroteskLatinUrl,
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootDocument,
  component: RootComponent,
  notFoundComponent: NotFound,
});
